<template>
  <v-dialog
    v-model="dialog"
    content-class="c-share-dialog"
    :fullscreen="$vuetify.breakpoint.xsAndDown"
    max-width="220px"
    min-width="208px"
    overlay-opacity="0.2"
    transition="scale-transition"
    width="80%"
    @click:outside="closeModal"
    @keydown.esc="closeModal"
  >
    <v-card align="center">
      <v-toolbar class="c-share-toolbar" dark dense color="primary">
        <v-toolbar-title>{{ $t('dialog.sharing.banner') }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="closeModal">
          <v-icon> mdi-close </v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="c-share-items justify-center pa-0 g-skinny-scrollbars">
        <v-list rounded>
          <v-list-item-group>
            <v-list-item
              v-for="network in activeNetworks"
              :key="network.key"
              class="c-share-item"
            >
              <ShareNetwork
                class="d-flex align-center"
                :click="network.click"
                :network="network.key"
                :url="encodedLink"
                :title="title"
                :description="abstract"
                tag="div"
              >
                <v-list-item-icon class="c-share-icon">
                  <v-btn bottom :color="network.colour" dark fab small>
                    <FontAwesomeIcon class="c-fa" :icon="network.icon" inverse />
                  </v-btn>
                </v-list-item-icon>
                <v-list-item-content
                  v-if="network.key === 'copy'"
                  v-tooltip="{ content: $t('dialog.sharing.copied'), theme: 'transient', shown: copied }"
                  class="text-left"
                >
                  <v-list-item-title class="text-h6">
                    {{ $t('dialog.sharing.copy') }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-content
                  v-else
                  class="text-left"
                >
                  <v-list-item-title class="text-h6">
                    {{ network.localize ? $t(`dialog.sharing.${network.key}`) : network.name }}
                  </v-list-item-title>
                </v-list-item-content>
              </ShareNetwork>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
// import Vue from 'vue'
// import VueSocialSharing from 'vue-social-sharing'
import ShareNetwork from '@/components/base/ShareNetwork'
import shareMixin from '@/mixins/shareMixin'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faEnvelope, faCommentDots, faLink } from '@fortawesome/free-solid-svg-icons'

import {
  faFacebook,
  faFacebookF,
  faFacebookMessenger,
  faLinkedin,
  faLinkedinIn,
  faTelegram,
  faXTwitter,
  faWhatsapp
} from '@fortawesome/free-brands-svg-icons'

library.add(faEnvelope, faCommentDots, faLink)
library.add(faFacebook, faFacebookMessenger, faLinkedin, faTelegram, faXTwitter, faWhatsapp)
library.add(faFacebookF, faLinkedinIn)

/*
Vue.use(VueSocialSharing, {
  networks: {
    copy: 'javascript:await window.navigator.clipboard.writeText(@url)'
  }
})
*/

export default {
  name: 'ShareModal',

  components: {
    ShareNetwork
  },

  mixins: [shareMixin],

  model: {
    prop: 'show',
    event: 'close'
  },

  props: {
    entity: {
      type: Object,
      required: true
    },

    show: {
      type: Boolean,
      required: true
    }
  },

  data: function () {
    return {
      dialog: this.show,
      copied: false,
      networks: [
        {
          key: 'copy',
          type: 'basic',
          name: 'Copy',
          localize: true,
          icon: ['fas', 'link'],
          colour: 'orange',
          success: 'copied',
          click: () => {
            this.copyToClipboard(this.encodedLink)
            this.copied = true
            setTimeout(() => (this.copied = false), 2000)
          }
        },
        {
          key: 'email',
          type: 'basic',
          name: 'Email',
          localize: true,
          icon: ['fas', 'envelope'],
          colour: '#0088cc'
        },
        {
          key: 'sms',
          type: 'basic',
          name: 'SMS',
          localize: false,
          icon: ['fas', 'comment-dots'],
          colour: '#6acc46'
        },
        {
          key: 'facebook',
          type: 'social',
          name: 'Facebook',
          localize: false,
          icon: ['fab', 'facebook-f'],
          colour: '#1877f2'
        },
        {
          key: 'linkedin',
          type: 'social',
          name: 'LinkedIn',
          localize: false,
          icon: ['fab', 'linkedin-in'],
          colour: '#007bb5'
        },
        {
          key: 'twitter',
          type: 'social',
          name: 'X',
          localize: false,
          icon: ['fab', 'x-twitter'],
          colour: '#000000'
        },
        {
          key: 'messenger',
          type: 'dm',
          name: 'Messenger',
          localize: false,
          icon: ['fab', 'facebook-messenger'],
          colour: '#0084ff'
        },
        {
          key: 'telegram',
          type: 'dm',
          name: 'Telegram',
          localize: false,
          icon: ['fab', 'telegram'],
          colour: '#0088cc'
        },
        {
          key: 'whatsapp',
          type: 'dm',
          name: 'Whatsapp',
          localize: false,
          icon: ['fab', 'whatsapp'],
          colour: '#25d366'
        }
      ]
    }
  },

  computed: {
    activeNetworks() {
      return this.$feature('social')
        ? this.networks
        : this.networks.filter((network) => network.type !== 'social')
    },

    entityId() {
      return this.entity.id
    },

    title() {
      return this.entity.title
    },

    abstract() {
      return this.entity.abstract
    },

    routeName() {
      return this.entity.routeName
    },

    shareableId() {
      return this.shareMixin_prepId(this.entityId)
    },

    encodedTitle() {
      return encodeURIComponent(this.title)
    },

    encodedAbstract() {
      return encodeURIComponent(this.abstract)
    },

    encodedLink() {
      return `${window.location.origin}/${this.routeName}/${this.shareableId}`
    },

    encodedSpacer() {
      return encodeURIComponent(`\n\n`)
    }
  },

  mounted: function () {
    document.addEventListener('keydown', this.onKeydown)
  },

  beforeDestroy: function () {
    document.removeEventListener('keydown', this.onKeydown)
  },

  methods: {
    async copyToClipboard(text) {
      try {
        if (window.navigator) {
          await window.navigator.clipboard.writeText(text)
        }
      } catch (error) {
        console.error('[ShareModal]: Failed to copy link: ', error)
        this.fallbackCopyToClipboard(text)
      }
    },

    closeModal() {
      this.dialog = false
      this.$emit('close', false)
    },

    doNothing() {
      return true
    },

    fallbackCopyToClipboard(text) {
      const dummy = document.createElement('input')
      document.body.appendChild(dummy)
      dummy.value = text
      dummy.select()
      dummy.focus()
      document.execCommand('copy')
      document.body.removeChild(dummy)
    },

    onKeydown(e) {
      if (e.key === 'Escape') {
        console.debug('[ShareModal]: Escape key pressed!')
        // this.closeModal()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.c-share-toolbar {
  flex-grow: 0;
}

.c-share-items {
  background-color: var(--v-background-base);
  overflow-y: auto;
  overflow-x: hidden;
  height: max(330px, 30vh);
  overscroll-behavior: contain;

  .c-share-item {
    height: 56px;
    border-radius: 24px !important;
    padding: 0px 8px;

    .c-share-icon {
      margin-right: 16px !important;
      margin-top: 8px;
      margin-bottom: 8px;

      .c-fa {
        font-size: 24px;
      }
    }
  }
}
</style>

<style lang="css">
.c-share-dialog:not(.v-dialog--fullscreen) {
}

.c-share-dialog {
  border: 2px solid var(--v-primary-base);
  border-radius: 32px;
  overflow: hidden;
}
</style>

